import { useTranslation } from 'react-i18next';

import {
    AuthRedirectType,
    flushTokens,
    useAppDispatch,
    useAppSelector,
    useLogout,
    useProfile,
    useTokens,
} from '@pkg/hooks';
import { LocalStorageKeys } from '@pkg/types';
import { removeLocalStorageItems } from '@pkg/utils';

import { apiFactory, Auth, Cart, Users } from '@app/client';
import { type Token } from '@app/client/common/structs';
import { getAppUrl } from '@app/helpers';
import { YAT_PICKER_CHECKOUT } from '@app/routes/paths';
import { setError } from '@app/store/actions/error';
import { addNotification } from '@app/store/actions/notification';
import { initializeUser } from '@app/store/actions/user';
import { type ErrorState } from '@app/store/types/error';
import { useAuthSidebar, useAuthTokens, useUser } from '@app/store/zustand';

export function useLogin() {
    const { t } = useTranslation(['waitlist']);
    const dispatch = useAppDispatch();
    const partnerConversionId = useAppSelector((rootState) => rootState.partnerIntegrations.partnerConversionId);

    const { setTokens } = useTokens();
    const { setIsLoading: setIsUserLoading } = useUser();
    const { redirectToProfile } = useProfile();
    const { closeAuthSidebar } = useAuthSidebar();
    const { currentUserId, token: sessionToken } = useAuthTokens();
    const { sanitizeSession } = useLogout();

    async function mergeDraftUser(draftUserId: string, draftUserAccessToken?: string) {
        const cartItems = await apiFactory().call(Cart.getCart, {}, { accessToken: draftUserAccessToken });
        if (!cartItems.ok) {
            return;
        }

        const hasPendingOrder = cartItems.value.order_items?.length > 0;

        try {
            if (hasPendingOrder && draftUserId) {
                const consumeDraftUser = await apiFactory().call(Users.consumeDraftUser, {
                    value: {
                        userId: draftUserId,
                    },
                });

                if (consumeDraftUser.ok) {
                    removeLocalStorageItems([LocalStorageKeys.DraftUserId]);
                } else {
                    throw new Error();
                }
            }
        } catch (error) {
            dispatch(
                addNotification({
                    message: t('waitlist_login_failed_consume'),
                    severity: 'error',
                }),
            );
        }
    }

    async function loginWithPassword({ email, password }: { email: string; password: string }): Promise<Token | void> {
        setIsUserLoading(true);

        try {
            const loginResult = await apiFactory().call(Auth.login, {
                value: {
                    email: email.trim(),
                    password: password.trim(),
                    partner_conversion_id: partnerConversionId,
                },
            });

            if (loginResult.ok) {
                const token = loginResult.value;
                return loginWithToken({ token });
            } else {
                throw new Error('AUTH_ERROR_LOGIN_FAILED');
            }
        } catch (error) {
            setIsUserLoading(false);
            flushTokens();
            dispatch(setError({ error: error as ErrorState, notify: true }));
        }
    }

    async function loginWithToken({ token }: { token: Token }) {
        setIsUserLoading(true);
        apiFactory().ws?.closeWithoutReconnect();

        // Store previous draft user id before setting new tokens
        const draftUserId = currentUserId ? currentUserId : undefined;
        const draftUserAccessToken = sessionToken.access_token;

        sanitizeSession();
        await setTokens(token);

        if (draftUserId) {
            await mergeDraftUser(draftUserId, draftUserAccessToken);
        }

        await dispatch(initializeUser());

        const redirectType = localStorage.getItem(LocalStorageKeys.AuthRedirectType);
        if (redirectType === AuthRedirectType.Disabled) {
            removeLocalStorageItems([LocalStorageKeys.AuthRedirectType]);

            // No redirect set, close auth side panel
            closeAuthSidebar();

            return token;
        } else {
            redirectAfterLogin();
        }
    }

    function redirectAfterLogin() {
        // Fecth redirect values from local storage
        const redirectType = localStorage.getItem(LocalStorageKeys.AuthRedirectType);
        const redirectPath = localStorage.getItem(LocalStorageKeys.AuthRedirectPath);

        /**
         * Now that we have consumed these redirect values from local storage, we can remove them
         * to avoid any potential redirect loop.
         */
        removeLocalStorageItems([LocalStorageKeys.AuthRedirectType, LocalStorageKeys.AuthRedirectType]);

        switch (redirectType) {
            case AuthRedirectType.Disabled:
                return false;

            case AuthRedirectType.Path:
                if (redirectPath) {
                    window.location.href = getAppUrl(redirectPath);
                } else {
                    redirectToProfile({ hardRedirect: true });
                }
                return;

            case AuthRedirectType.Checkout:
                window.location.href = getAppUrl(YAT_PICKER_CHECKOUT);
                return;

            case AuthRedirectType.Profile:
            default:
                redirectToProfile({ hardRedirect: true });
                return;
        }
    }

    return {
        loginWithPassword,
        loginWithToken,
        redirectAfterLogin,
    };
}
